import { userApi } from '@/app/redux/endpoints/user';
import { useAppDispatch } from '@/app/redux/hooks';
import { useUser } from '@/app/redux/hooks/user';
import {
  authenticateUser,
  setClaims,
  unauthenticateUser,
} from '@/app/redux/slices/sessionSlice';
import { getProfileGate } from '@/auth/profileReadyGate';
import { GlobalStyles } from '@/common/ui/styles/globalStyles';
import {
  enforceAutoLogout,
  handleLogout,
  refreshUserClaims,
} from '@/common/utils/auth';
import {
  clearMixpanelUser,
  identifyMixpanelUser,
} from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import router from './router';

export default function App() {
  const dispatch = useAppDispatch();

  const { user } = useUser();

  useEffect(() => {
    const unsub = onAuthStateChanged(api.asEducator.auth, async (authUser) => {
      if (!authUser) {
        dispatch(unauthenticateUser());
        clearMixpanelUser('educator');
        return;
      }

      const didAutoLogout = enforceAutoLogout('educator', handleLogout);
      if (didAutoLogout) return;

      dispatch(
        authenticateUser({
          uid: authUser.uid,
          email: authUser.email!,
          isEmailVerified: authUser.emailVerified,
        }),
      );
      const profileGate = getProfileGate();
      if (profileGate) {
        // profileGate ensures profile has been created in Firestore
        // before we try fetching it/syncing claims
        await profileGate;
        dispatch(
          userApi.endpoints.getUser.initiate(authUser.uid, {
            forceRefetch: true,
          }),
        );
      }

      dispatch(setClaims(null));
      const claims = await refreshUserClaims((c) => !!c?.role);
      if (claims) dispatch(setClaims(claims));
    });
    return () => unsub();
  }, [dispatch]);

  useEffect(() => {
    const orgId = user?.organisationIds[0];
    if (orgId) {
      identifyMixpanelUser('educator', user.uid, orgId);
    }
  }, [user]);

  return (
    <>
      <Toaster position="top-right" richColors closeButton expand />
      <GlobalStyles />
      <RouterProvider router={router} />
    </>
  );
}
